'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Link, usePathname } from '@/i18n/navigation';
import { MenuIcon, XIcon, ChevronDown, ChevronRight, BookOpenIcon, PackageSearchIcon } from 'lucide-react';
import { BrandLogo, AuthDropdown, CartButton, LocaleSwitcher, TopSearchBar, SocialMediaLinks } from '@/components/partials';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useScrollDirection, useMetadata } from '@/hooks';
import { tOptional, getResizedPictureUrl } from '@/lib/utils';
import { type FileType } from '@/types/file';
type MenuItemType = {
  label: string;
  href: string;
  submenu?: {
    label: string;
    href: string;
    picture?: FileType;
  }[];
};
type PropsType = {
  menuItems: MenuItemType[];
  productsRoute: string;
  productRoute: string;
  photoGalleriesRoute: string;
  cataloguesRoute: string;
  orderTrackRoute: string;
  hasToken: boolean;
};

// Utility function to check if a submenu item is active
const isSubmenuItemActiveCheck = (subItem: {
  href: string;
}, pathname: string, parentHref: string, productsRoute: string, productRoute: string, photoGalleriesRoute: string): boolean => {
  // The category chain sits directly in a product-detail URL
  // (/product/<categoryChain>/<productSlug>[/brand/model/oem]). Match the category slug
  // as a whole path SEGMENT — never a bare substring, or a product whose slug merely
  // CONTAINS a category name (e.g. "differential-carrier-gear" under /engine) would
  // wrongly light up the unrelated "Differential" category.
  const categorySlug = subItem.href.replace(`${productsRoute}/`, '');
  const isProductDetailMatch = parentHref === productsRoute && pathname.startsWith(productRoute + '/') && pathname.split('/').includes(categorySlug);
  return pathname === subItem.href || pathname.startsWith(subItem.href + '/') || isProductDetailMatch || subItem.href === photoGalleriesRoute && pathname.startsWith(`${photoGalleriesRoute}/`);
};

// Utility function to check if the menu item is active
const isMenuItemActive = (item: MenuItemType, pathname: string, productsRoute: string, productRoute: string, photoGalleriesRoute: string): boolean => {
  const hasSubmenu = 'submenu' in item && item.submenu;

  // Check if any submenu item is active
  const isSubmenuItemActive = hasSubmenu && item.submenu ? item.submenu.some(subItem => isSubmenuItemActiveCheck(subItem, pathname, item.href, productsRoute, productRoute, photoGalleriesRoute)) : false;

  // Check if this is the products menu and we're on the product detail page
  const isProductRoute = item.href === productsRoute && pathname.startsWith(productRoute + '/');
  return item.href === '/' ? pathname === item.href : pathname.startsWith(item.href) || isSubmenuItemActive || isProductRoute;
};
const AppHeaderClient = t0 => {
  const $ = _c(153);
  const {
    menuItems,
    productsRoute,
    productRoute,
    photoGalleriesRoute,
    cataloguesRoute,
    orderTrackRoute,
    hasToken
  } = t0;
  const t = useTranslations();
  const pathname = usePathname();
  const {
    scrollDirection,
    isAtTop
  } = useScrollDirection();
  const {
    activeLocales
  } = useMetadata();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
  const [clickedSubmenu, setClickedSubmenu] = useState(null);
  const {
    socialMediaLinks
  } = useMetadata();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsMobileMenuOpen(false);
      setClickedSubmenu(null);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pathname) {
    t2 = [pathname];
    $[1] = pathname;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== menuItems || $[4] !== pathname) {
    t3 = () => {
      menuItems.forEach(item => {
        if ("submenu" in item && item.submenu) {
          const isInSubmenu = item.submenu.some(subItem => pathname === subItem.href || subItem.href === "/corporate/photo-galleries" && pathname.startsWith("/corporate/photo-galleries/"));
          if (isInSubmenu) {
            setOpenMobileSubmenu(item.label);
          }
        }
      });
    };
    t4 = [pathname, menuItems];
    $[3] = menuItems;
    $[4] = pathname;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== isMobileMenuOpen) {
    t5 = () => {
      if (isMobileMenuOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      return _temp;
    };
    t6 = [isMobileMenuOpen];
    $[7] = isMobileMenuOpen;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== isMobileMenuOpen) {
    t7 = () => {
      const mediaQuery = window.matchMedia("(min-width: 768px)");
      const handleChange = e => {
        if (e.matches && isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
        }
      };
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    };
    t8 = [isMobileMenuOpen];
    $[10] = isMobileMenuOpen;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[13] !== isMobileMenuOpen) {
    t9 = () => {
      const handleKeyDown = e_0 => {
        if (e_0.key === "Escape" && isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t10 = [isMobileMenuOpen];
    $[13] = isMobileMenuOpen;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  const t11 = `fixed top-0 z-50 w-full transition-transform duration-300 ease-in-out ${scrollDirection === "down" ? "-translate-y-full" : "translate-y-0"}`;
  const t12 = `bg-brand border-brand/30 relative z-40 grid w-full items-center gap-3 border-b px-5 transition-[height] duration-500 ease-in-out ${isAtTop ? "h-16 md:h-24" : "h-16"} grid-cols-2 md:grid-cols-3`;
  const t13 = `flex h-full items-center justify-start transition-[width] duration-500 ease-in-out ${isAtTop ? "w-36 md:w-44 lg:w-64" : "w-36 md:w-40 lg:w-44"}`;
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <BrandLogo />;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t13) {
    t15 = <div className="h-full w-full md:col-span-1"><div className={t13}>{t14}</div></div>;
    $[17] = t13;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== productsRoute) {
    t16 = <div className="hidden h-full w-full items-center justify-center md:flex"><div className="w-full max-w-md px-4"><TopSearchBar route={productsRoute} /></div></div>;
    $[19] = productsRoute;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <BookOpenIcon className="size-4 md:size-5" />;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== t) {
    t18 = t("catalogues");
    $[22] = t;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t18) {
    t19 = <span className="hidden text-[9px] leading-none font-medium uppercase opacity-80 lg:block">{t18}</span>;
    $[24] = t18;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== cataloguesRoute || $[27] !== t19) {
    t20 = <TooltipTrigger asChild={true}><Button variant="ghost" className="hover:bg-brand-darken h-7! w-7! shrink-0 flex-col items-center p-0 hover:text-white md:h-9! md:w-9! lg:h-auto! lg:w-auto! lg:gap-0.5 lg:px-2 lg:py-1" asChild={true}><Link href={cataloguesRoute}>{t17}{t19}</Link></Button></TooltipTrigger>;
    $[26] = cataloguesRoute;
    $[27] = t19;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== t) {
    t21 = t("catalogues");
    $[29] = t;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== t21) {
    t22 = <TooltipContent><p>{t21}</p></TooltipContent>;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== t20 || $[34] !== t22) {
    t23 = <Tooltip>{t20}{t22}</Tooltip>;
    $[33] = t20;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="mx-0.5 h-7 w-px shrink-0 bg-black/20 md:mx-1 md:h-9" />;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <PackageSearchIcon className="size-4 md:size-5" />;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== t) {
    t26 = t("order_tracking");
    $[38] = t;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== t26) {
    t27 = <span className="hidden text-[9px] leading-none font-medium uppercase opacity-80 lg:block">{t26}</span>;
    $[40] = t26;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== orderTrackRoute || $[43] !== t27) {
    t28 = <TooltipTrigger asChild={true}><Button variant="ghost" className="hover:bg-brand-darken h-7! w-7! shrink-0 flex-col items-center p-0 hover:text-white md:h-9! md:w-9! lg:h-auto! lg:w-auto! lg:gap-0.5 lg:px-2 lg:py-1" asChild={true}><Link href={orderTrackRoute}>{t25}{t27}</Link></Button></TooltipTrigger>;
    $[42] = orderTrackRoute;
    $[43] = t27;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== t) {
    t29 = t("order_tracking");
    $[45] = t;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== t29) {
    t30 = <TooltipContent><p>{t29}</p></TooltipContent>;
    $[47] = t29;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] !== t28 || $[50] !== t30) {
    t31 = <Tooltip>{t28}{t30}</Tooltip>;
    $[49] = t28;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div className="mx-0.5 h-7 w-px shrink-0 bg-black/20 md:mx-2 md:h-9" />;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  let t33;
  if ($[53] !== activeLocales) {
    t33 = <LocaleSwitcher locales={activeLocales} />;
    $[53] = activeLocales;
    $[54] = t33;
  } else {
    t33 = $[54];
  }
  let t34;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="mx-0.5 h-7 w-px shrink-0 bg-black/20 md:mx-1 md:h-9" />;
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  let t35;
  if ($[56] !== hasToken) {
    t35 = <AuthDropdown hasToken={hasToken} />;
    $[56] = hasToken;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  let t36;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className="mx-0.5 h-7 w-px shrink-0 bg-black/20 md:mx-1 md:h-9" />;
    $[58] = t36;
  } else {
    t36 = $[58];
  }
  let t37;
  if ($[59] !== hasToken) {
    t37 = <CartButton hasToken={hasToken} />;
    $[59] = hasToken;
    $[60] = t37;
  } else {
    t37 = $[60];
  }
  let t38;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className="mx-0.5 hidden h-7 w-px shrink-0 bg-black/20 lg:mx-2 lg:h-9 xl:block" />;
    $[61] = t38;
  } else {
    t38 = $[61];
  }
  let t39;
  if ($[62] !== t) {
    t39 = t("products");
    $[62] = t;
    $[63] = t39;
  } else {
    t39 = $[63];
  }
  let t40;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <ChevronRight className="size-4" />;
    $[64] = t40;
  } else {
    t40 = $[64];
  }
  let t41;
  if ($[65] !== productsRoute || $[66] !== t39) {
    t41 = <Button variant="default" className="hidden shrink-0 items-center gap-1 bg-black px-3 py-2 text-sm font-normal text-white uppercase transition-colors duration-300 hover:bg-black/80 xl:flex" asChild={true}><Link href={productsRoute}>{t39}{t40}</Link></Button>;
    $[65] = productsRoute;
    $[66] = t39;
    $[67] = t41;
  } else {
    t41 = $[67];
  }
  let t42;
  if ($[68] !== isMobileMenuOpen) {
    t42 = () => setIsMobileMenuOpen(!isMobileMenuOpen);
    $[68] = isMobileMenuOpen;
    $[69] = t42;
  } else {
    t42 = $[69];
  }
  const t43 = `hover:bg-brand-darken ml-1 shrink-0 transition-all duration-300 hover:text-white md:hidden ${isMobileMenuOpen ? "bg-brand-darken text-white" : ""}`;
  let t44;
  if ($[70] !== isMobileMenuOpen) {
    t44 = isMobileMenuOpen ? <XIcon className="size-6 transition-all duration-300" /> : <MenuIcon className="size-6 transition-all duration-300" />;
    $[70] = isMobileMenuOpen;
    $[71] = t44;
  } else {
    t44 = $[71];
  }
  let t45;
  if ($[72] !== t42 || $[73] !== t43 || $[74] !== t44) {
    t45 = <Button size="icon" variant="ghost" onClick={t42} className={t43} aria-label="Toggle menu">{t44}</Button>;
    $[72] = t42;
    $[73] = t43;
    $[74] = t44;
    $[75] = t45;
  } else {
    t45 = $[75];
  }
  let t46;
  if ($[76] !== t23 || $[77] !== t31 || $[78] !== t33 || $[79] !== t35 || $[80] !== t37 || $[81] !== t41 || $[82] !== t45) {
    t46 = <div className="flex h-full min-w-0 flex-1 items-center justify-end gap-0 select-none md:col-span-1">{t23}{t24}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t41}{t45}</div>;
    $[76] = t23;
    $[77] = t31;
    $[78] = t33;
    $[79] = t35;
    $[80] = t37;
    $[81] = t41;
    $[82] = t45;
    $[83] = t46;
  } else {
    t46 = $[83];
  }
  let t47;
  if ($[84] !== t12 || $[85] !== t15 || $[86] !== t16 || $[87] !== t46) {
    t47 = <div className={t12}>{t15}{t16}{t46}</div>;
    $[84] = t12;
    $[85] = t15;
    $[86] = t16;
    $[87] = t46;
    $[88] = t47;
  } else {
    t47 = $[88];
  }
  const t48 = `fixed inset-x-0 top-14 h-[calc(100dvh-56px)] bg-gray-100/80 backdrop-blur-sm transition-all duration-300 ease-in-out select-none md:hidden ${isMobileMenuOpen ? "pointer-events-auto visible flex translate-y-0 opacity-100" : "pointer-events-none invisible -translate-y-full opacity-0"}`;
  let t49;
  if ($[89] !== menuItems || $[90] !== openMobileSubmenu || $[91] !== pathname || $[92] !== photoGalleriesRoute || $[93] !== productRoute || $[94] !== productsRoute || $[95] !== t) {
    let t50;
    if ($[97] !== openMobileSubmenu || $[98] !== pathname || $[99] !== photoGalleriesRoute || $[100] !== productRoute || $[101] !== productsRoute || $[102] !== t) {
      t50 = item_0 => {
        const hasSubmenu = "submenu" in item_0 && item_0.submenu && item_0.submenu.length > 0;
        const isSubmenuOpen = openMobileSubmenu === item_0.label;
        const isActive = isMenuItemActive(item_0, pathname, productsRoute, productRoute, photoGalleriesRoute);
        return <li key={item_0.label} className="block w-full uppercase">{hasSubmenu ? <div className="w-full"><button onClick={() => setOpenMobileSubmenu(isSubmenuOpen ? null : item_0.label)} className={`flex w-full items-center justify-between rounded-lg px-4 py-3 uppercase transition-all duration-300 hover:pr-4 hover:pl-9 hover:backdrop-blur-sm ${isActive ? "bg-gray-200 text-gray-900 hover:bg-gray-300" : "hover:bg-brand-darken/80 hover:text-white"}`}><span>{tOptional(t, item_0.label)}</span><ChevronDown className={`h-5 w-5 transition-transform duration-300 ${isSubmenuOpen ? "rotate-180" : ""}`} /></button><div className={`overflow-hidden transition-all duration-300 ${isSubmenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"}`}><ul className="mt-2 space-y-2 pl-4">{item_0.submenu?.map((subItem_0, key) => {
                  const isSubActive = isSubmenuItemActiveCheck(subItem_0, pathname, item_0.href, productsRoute, productRoute, photoGalleriesRoute);
                  return <li key={key}><Link href={subItem_0.href} onClick={() => setIsMobileMenuOpen(false)} className={`flex items-center gap-2 rounded-lg px-4 py-2 text-lg transition-all duration-300 ${isSubActive ? "bg-brand/30 font-bold text-gray-900" : "text-gray-700 hover:bg-gray-200 hover:text-gray-900"}`}>{subItem_0.picture ? <div className="relative h-6 w-6 shrink-0"><Image src={getResizedPictureUrl(subItem_0.picture.path, 48, 48)} alt={subItem_0.label} fill={true} sizes="24px" className="object-contain" loading="eager" priority={true} /></div> : null}<span>{tOptional(t, subItem_0.label)}</span></Link></li>;
                })}</ul></div></div> : <Link href={item_0.href} onClick={() => setIsMobileMenuOpen(false)} className={`block w-full rounded-lg px-4 py-3 transition-all duration-300 hover:pr-4 hover:pl-9 hover:backdrop-blur-sm ${isActive ? "bg-gray-200 text-gray-900 hover:bg-gray-300" : "hover:bg-brand-darken/80 hover:text-white"}`}>{tOptional(t, item_0.label)}</Link>}</li>;
      };
      $[97] = openMobileSubmenu;
      $[98] = pathname;
      $[99] = photoGalleriesRoute;
      $[100] = productRoute;
      $[101] = productsRoute;
      $[102] = t;
      $[103] = t50;
    } else {
      t50 = $[103];
    }
    t49 = menuItems.map(t50);
    $[89] = menuItems;
    $[90] = openMobileSubmenu;
    $[91] = pathname;
    $[92] = photoGalleriesRoute;
    $[93] = productRoute;
    $[94] = productsRoute;
    $[95] = t;
    $[96] = t49;
  } else {
    t49 = $[96];
  }
  let t50;
  if ($[104] !== t49) {
    t50 = <ul className="mobile-menu flex w-full flex-col items-center space-y-3 text-2xl font-bold">{t49}</ul>;
    $[104] = t49;
    $[105] = t50;
  } else {
    t50 = $[105];
  }
  let t51;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = () => setIsMobileMenuOpen(false);
    $[106] = t51;
  } else {
    t51 = $[106];
  }
  const t52 = `flex w-full items-center gap-3 rounded-lg px-4 py-3 text-2xl font-bold uppercase transition-all duration-300 hover:pr-4 hover:pl-9 hover:backdrop-blur-sm ${pathname.startsWith(orderTrackRoute) ? "bg-gray-200 text-gray-900 hover:bg-gray-300" : "hover:bg-brand-darken/80 hover:text-white"}`;
  let t53;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <PackageSearchIcon className="size-6 shrink-0" />;
    $[107] = t53;
  } else {
    t53 = $[107];
  }
  let t54;
  if ($[108] !== t) {
    t54 = t("order_tracking");
    $[108] = t;
    $[109] = t54;
  } else {
    t54 = $[109];
  }
  let t55;
  if ($[110] !== orderTrackRoute || $[111] !== t52 || $[112] !== t54) {
    t55 = <Link href={orderTrackRoute} onClick={t51} className={t52}>{t53}{t54}</Link>;
    $[110] = orderTrackRoute;
    $[111] = t52;
    $[112] = t54;
    $[113] = t55;
  } else {
    t55 = $[113];
  }
  let t56;
  if ($[114] !== productsRoute) {
    t56 = <TopSearchBar route={productsRoute} mobile={true} />;
    $[114] = productsRoute;
    $[115] = t56;
  } else {
    t56 = $[115];
  }
  let t57;
  if ($[116] !== socialMediaLinks) {
    t57 = <SocialMediaLinks links={socialMediaLinks} />;
    $[116] = socialMediaLinks;
    $[117] = t57;
  } else {
    t57 = $[117];
  }
  let t58;
  if ($[118] !== t50 || $[119] !== t55 || $[120] !== t56 || $[121] !== t57) {
    t58 = <ScrollArea className="h-full w-full" viewportClassName="h-full overscroll-contain" thumbClassName="bg-gray-600/80 hover:bg-gray-700"><div className="flex flex-col space-y-5 p-5">{t50}{t55}{t56}{t57}</div></ScrollArea>;
    $[118] = t50;
    $[119] = t55;
    $[120] = t56;
    $[121] = t57;
    $[122] = t58;
  } else {
    t58 = $[122];
  }
  let t59;
  if ($[123] !== t48 || $[124] !== t58) {
    t59 = <nav className={t48}>{t58}</nav>;
    $[123] = t48;
    $[124] = t58;
    $[125] = t59;
  } else {
    t59 = $[125];
  }
  let t60;
  if ($[126] !== clickedSubmenu || $[127] !== menuItems || $[128] !== pathname || $[129] !== photoGalleriesRoute || $[130] !== productRoute || $[131] !== productsRoute || $[132] !== t) {
    let t61;
    if ($[134] !== clickedSubmenu || $[135] !== pathname || $[136] !== photoGalleriesRoute || $[137] !== productRoute || $[138] !== productsRoute || $[139] !== t) {
      t61 = item_1 => {
        const hasSubmenu_0 = "submenu" in item_1 && item_1.submenu;
        const isActive_0 = isMenuItemActive(item_1, pathname, productsRoute, productRoute, photoGalleriesRoute);
        return <li key={item_1.label} className={`group relative flex-1 rounded-xs uppercase transition-all duration-300 ${hasSubmenu_0 ? isActive_0 ? "bg-brand/20 hover:bg-brand/40 text-gray-950 hover:text-gray-950" : "text-gray-700 hover:bg-gray-200 hover:text-gray-900" : isActive_0 ? "bg-brand/20 hover:bg-brand/40 text-gray-950 hover:text-gray-950" : "text-gray-700 hover:bg-gray-200 hover:text-gray-900"}`}>{hasSubmenu_0 && item_1.submenu && item_1.submenu.length > 0 ? <><Link href={item_1.href} className="flex items-center justify-center gap-1 px-2.5 py-1.5 transition-all duration-300">{tOptional(t, item_1.label)}<ChevronDown className="h-3 w-3 transition-transform duration-300 group-hover:rotate-180" /></Link><div className={`absolute top-full left-0 z-50 pt-3 ${clickedSubmenu === item_1.label ? "pointer-events-none hidden" : "hidden group-hover:block"}`} onMouseLeave={() => setClickedSubmenu(null)}><div className="animate-in fade-in zoom-in-95 slide-in-from-top-2 min-w-56 origin-top overflow-hidden rounded-lg border border-gray-300/50 bg-white/85 shadow-2xl backdrop-blur-sm duration-200 md:min-w-64"><ul className="py-1.5">{item_1.submenu?.map((subItem_1, key_0) => {
                    const isSubActive_0 = isSubmenuItemActiveCheck(subItem_1, pathname, item_1.href, productsRoute, productRoute, photoGalleriesRoute);
                    return <li key={key_0} className="relative"><Link href={subItem_1.href} onClick={() => {
                        setClickedSubmenu(item_1.label);
                      }} className={`group/item relative flex items-center gap-2 overflow-hidden px-4 py-1.5 text-sm transition-all duration-300 ${isSubActive_0 ? "text-brand-darken" : "hover:text-brand-darken text-gray-700"}`}><svg className="text-brand h-3 w-3 shrink-0 -translate-x-2 opacity-0 transition-all duration-300 group-hover/item:translate-x-0 group-hover/item:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg><span className="relative z-10 -translate-x-3 transition-transform duration-300 group-hover/item:translate-x-0">{tOptional(t, subItem_1.label)}</span>{subItem_1.picture && <div className="relative ml-auto h-5 w-5 shrink-0"><Image src={getResizedPictureUrl(subItem_1.picture.path, 40, 40)} alt={subItem_1.label} fill={true} sizes="20px" className="object-contain transition-all duration-300 group-hover/item:filter-[brightness(0)_saturate(100%)_invert(72%)_sepia(85%)_saturate(900%)_hue-rotate(1deg)_brightness(96%)_contrast(94%)]" loading="eager" priority={true} /></div>}<div className="from-brand to-brand-darken absolute right-3 bottom-0 left-3 z-20 h-px origin-left scale-x-0 bg-linear-to-r opacity-0 transition-all duration-300 group-hover/item:scale-x-100 group-hover/item:opacity-100" /><div className={`from-brand/5 via-brand/3 absolute inset-0 bg-linear-to-r to-transparent transition-opacity duration-300 ${isSubActive_0 ? "opacity-100" : "opacity-0 group-hover/item:opacity-100"}`} /></Link>{key_0 !== item_1.submenu.length - 1 && <div className="absolute right-3 bottom-0 left-3 z-0 border-t border-gray-200/60" />}</li>;
                  })}</ul></div></div></> : <Link href={item_1.href} className="flex items-center justify-center px-2.5 py-1.5 font-medium transition-all duration-300">{tOptional(t, item_1.label)}</Link>}</li>;
      };
      $[134] = clickedSubmenu;
      $[135] = pathname;
      $[136] = photoGalleriesRoute;
      $[137] = productRoute;
      $[138] = productsRoute;
      $[139] = t;
      $[140] = t61;
    } else {
      t61 = $[140];
    }
    t60 = menuItems.map(t61);
    $[126] = clickedSubmenu;
    $[127] = menuItems;
    $[128] = pathname;
    $[129] = photoGalleriesRoute;
    $[130] = productRoute;
    $[131] = productsRoute;
    $[132] = t;
    $[133] = t60;
  } else {
    t60 = $[133];
  }
  let t61;
  if ($[141] !== t60) {
    t61 = <ul className="main-menu flex h-full w-full flex-row items-center justify-between space-x-1 text-sm font-medium transition-all duration-300 lg:w-auto lg:justify-start">{t60}</ul>;
    $[141] = t60;
    $[142] = t61;
  } else {
    t61 = $[142];
  }
  let t62;
  if ($[143] !== socialMediaLinks) {
    t62 = <SocialMediaLinks links={socialMediaLinks} size="xs" className="hidden transition-all duration-300 lg:flex" />;
    $[143] = socialMediaLinks;
    $[144] = t62;
  } else {
    t62 = $[144];
  }
  let t63;
  if ($[145] !== t61 || $[146] !== t62) {
    t63 = <nav className="hidden h-10 items-center justify-center border bg-gray-100 px-5 transition-all duration-300 select-none md:flex lg:justify-between">{t61}{t62}</nav>;
    $[145] = t61;
    $[146] = t62;
    $[147] = t63;
  } else {
    t63 = $[147];
  }
  let t64;
  if ($[148] !== t11 || $[149] !== t47 || $[150] !== t59 || $[151] !== t63) {
    t64 = <header className={t11}>{t47}{t59}{t63}</header>;
    $[148] = t11;
    $[149] = t47;
    $[150] = t59;
    $[151] = t63;
    $[152] = t64;
  } else {
    t64 = $[152];
  }
  return t64;
};
export default AppHeaderClient;
function _temp() {
  document.body.style.overflow = "";
}